/**
 * Derive a loan type's status content from another loan type's.
 *
 * Balance Transfer, Plot Loan and Other were seeded by cloning LAP's
 * application status codes and replacing the prefix, so OPTBT07 carries the
 * same title and description as OPTLAP07 and means the same point in the
 * journey. Their dashboard content is therefore LAP's with the codes
 * rewritten, and deriving it keeps one copy rather than three near-identical
 * 40KB files that would drift apart on the first copy edit.
 *
 * This is NOT a general rule about status code suffixes. UBL, PL and GL run
 * different journeys, so the same suffix means different things there - LAP03
 * is Property Details where UBL03 is Digio KYC. Only families actually cloned
 * from a template may be derived this way.
 */

const codeFor = (key, fromCode, toCode) =>
    key.startsWith(`OPT${fromCode}`)
        ? `OPT${toCode}${key.slice(`OPT${fromCode}`.length)}`
        : key;

/**
 * Rewrite the keys of a status dictionary, cloning each entry.
 *
 * The clone is load-bearing. withStatusOrder assigns `.order` by mutating the
 * objects handed to it, and a status object is shared between groups -
 * OPTLAP02 sits in the active, rejected and cancelled sets. Aliasing the
 * originals here would let one loan type's ordering overwrite another's.
 */
const rewriteKeys = (statuses, fromCode, toCode) =>
    Object.fromEntries(
        Object.entries(statuses).map(([key, status]) => [
            codeFor(key, fromCode, toCode),
            { ...status },
        ])
    );

/**
 * Build one derived family.
 *
 * `getAll` is a function rather than a dictionary, and the template's version
 * compares its argument against its own literals (OPTLAP09, OPTLAP10). So the
 * incoming code is translated back into the template's space before the call,
 * and the result translated out again.
 */
export const deriveStatusFamily = (template, fromCode, toCode) => {
    const derived = {};

    for (const [group, value] of Object.entries(template)) {
        if (typeof value === 'function') {
            derived[group] = (currentStatusCode) =>
                rewriteKeys(
                    value(codeFor(currentStatusCode ?? '', toCode, fromCode)),
                    fromCode,
                    toCode
                );
        } else {
            derived[group] = rewriteKeys(value, fromCode, toCode);
        }
    }

    return derived;
};
